import React from 'react';
import { Typography, Button, Paper, Stack } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionKey } from '../../system/permissions';
import ResponsiveLayout from './ResponsiveLayout';

/**
 * Hard gate for users whose system has granted them zero permissions — i.e.
 * no VIEW_LISTING and no LISTING_ACCESS_PENDING. On mlsnow that's the NA
 * (No Access) MemberClass; on Bright that's a member with no effective
 * listing permissions. Either way we stop them at the door with a clear
 * "contact your administrator" message instead of letting them fall into a
 * broken empty state.
 *
 * Ordering matters: this gate must sit INSIDE ListingAccessPendingGate so
 * Personal Assistants (who legitimately have only LISTING_ACCESS_PENDING
 * and no VIEW_LISTING) see the friendly PA banner instead of the hard NA
 * message.
 */
const NoAccessGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { hasPermissionKey } = useAuth();

  if (hasPermissionKey(PermissionKey.VIEW_LISTING)) {
    return <>{children}</>;
  }

  return (
    <ResponsiveLayout>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 600,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ color: 'secondary.medium' }}
        >
          Listing Management Access Required
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Your account does not currently have access to listing management.
          Please reach out to your administrator to request access.
        </Typography>
        <Stack spacing={2} direction="row" justifyContent="center">
          <Button
            variant="contained"
            href="https://support.brightmls.com/s/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Support
          </Button>
        </Stack>
      </Paper>
    </ResponsiveLayout>
  );
};

export default NoAccessGate;
